import { Check, X } from 'lucide-react';
import React from 'react';
import Button from '../../../../components/Button';
import type { QuestTemplateFieldToCollect } from '../../services/questTypes';

interface FieldToCollectEditProps {
    field: QuestTemplateFieldToCollect;
    errors: { [key: string]: string };
    onSave: (field: QuestTemplateFieldToCollect) => void;
    onCancel: () => void;
    onFieldChange: (key: keyof QuestTemplateFieldToCollect, value: string | number) => void;
}

export const FieldToCollectEdit: React.FC<FieldToCollectEditProps> = ({
    field,
    errors,
    onSave,
    onCancel,
    onFieldChange
}) => {
    const handleSave = () => {
        onSave(field);
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Order
                    </label>
                    <input
                        type="number"
                        min="0"
                        value={field.order ?? 0}
                        onChange={(e) => onFieldChange('order', parseInt(e.target.value) || 0)}
                        className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.order ? 'border-red-300' : 'border-gray-300'
                            }`}
                    />
                    {errors.order && (
                        <p className="mt-1 text-sm text-red-600">{errors.order}</p>
                    )}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Key *
                    </label>
                    <input
                        type="text"
                        value={field.key || ''}
                        onChange={(e) => onFieldChange('key', e.target.value)}
                        placeholder="e.g., user_mood, sleep_hours"
                        className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.key ? 'border-red-300' : 'border-gray-300'
                            }`}
                    />
                    {errors.key && (
                        <p className="mt-1 text-sm text-red-600">{errors.key}</p>
                    )}
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                </label>
                <textarea
                    value={field.description || ''}
                    onChange={(e) => onFieldChange('description', e.target.value)}
                    placeholder="Description of the field to collect for the AI to use"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${errors.description ? 'border-red-300' : 'border-gray-300'
                        }`}
                />
                {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    AI Guidance *
                </label>
                <textarea
                    value={field.aiGuidance || ''}
                    onChange={(e) => onFieldChange('aiGuidance', e.target.value)}
                    placeholder="AI guidance for the field to collect"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${errors.aiGuidance ? 'border-red-300' : 'border-gray-300'
                        }`}
                />
                {errors.aiGuidance && (
                    <p className="mt-1 text-sm text-red-600">{errors.aiGuidance}</p>
                )}
            </div>
            <div className="flex items-center gap-2 pt-2 justify-end">
                <Button
                    variant="success"
                    size="sm"
                    icon={<Check className="w-4 h-4" />}
                    onClick={handleSave}
                >
                    Save
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    icon={<X className="w-4 h-4" />}
                    onClick={onCancel}
                >
                    Cancel
                </Button>
            </div>
        </div>
    );
};